// kalanjay/pages/KalanjayBatchDetail.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Collapse,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Tabs,
  Tab,
  FormHelperText,
  Stack,
  InputAdornment,
} from "@mui/material";

import * as yup from "yup";
import { formatWithTzLabel } from "../../../utils/dateUtils";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import AppTable from "../../../common/app-table";
import DashboardBadge from "../../../common/dashboard-badges";
import ButtonOne from "../../../common/normal-button";
import AppDatePicker from "../../../common/app-date-picker";
import AppChip from "../../../common/app-chip";
import typography from "../../../cssUtils/typography";
import {
  ColorsConstants,
  borderColors,
  dashboardCardBgColor,
  grayFontColor,
  inputColor,
  dashboardCardGreenTextColor,
} from "../../../cssUtils/constants";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";
import {
  CREATE_KALANJAY_EXAM,
  CREATE_KALANJAY_FEE,
  CREATE_KALANJAY_STUDENT,
  DELETE_KALANJAY_EXAM,
  DELETE_KALANJAY_FEE,
  DELETE_KALANJAY_STUDENT,
  GET_KALANJAY_BATCH_STATS,
  GET_KALANJAY_BATCH_STUDENTS,
  GET_KALANJAY_BATCHES,
  GET_KALANJAY_STUDENT_DETAIL,
  GET_KALANJAY_STUDENTS,
  MARK_KALANJAY_FEE_PAID,
  UPDATE_KALANJAY_BATCH_ENROLLMENT,
  UPDATE_KALANJAY_FEE,
  UPDATE_KALANJAY_STUDENT,
} from "../../../config/urls";
import { useAuth } from "../../../hooks/AuthProvider";
import NoPermission from "../../../common/NoPermission";
import { Search } from "@mui/icons-material";

// ── constants ─────────────────────────────────────────────────────────────────
const FEE_TYPES = ["Yearly Fees"];
const PAY_MODES = ["UPI", "Cash", "Cheque", "Bank Transfer", "Other"];
const DURATIONS = [
  "2024-25",
  "2025-26",
  "2026-27",
  "2027-28",
  "2028-29",
  "2029-30",
];
const EXAM_YEARS = [
  "1st Year",
  "2nd Year",
  "3rd Year",
  "4th Year",
  "5th Year",
  "6th Year",
  "Visharad",
  "Masters",
  "Diploma",
];
const SCHOOL_GRADES = [
  "Pre-Primary",
  "Class 1",
  "Class 2",
  "Class 3",
  "Class 4",
  "Class 5",
  "Class 6",
  "Class 7",
  "Class 8",
  "Class 9",
  "Class 10",
  "Class 11",
  "Class 12",
  "College",
];
const CENTRES = ["Juhu", "Grant Road"];
const inr = (n) => "₹" + Number(n).toLocaleString("en-IN");
const isOD = (due, st) => st !== "Paid" && new Date(due) < new Date();
const effSt = (r) => (isOD(r.due, r.status) ? "Overdue" : r.status);
const mkIni = (n) =>
  n
    .trim()
    .split(" ")
    .map((x) => x[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();
const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d || "—";
  }
};
const fmtDT = (d) => {
  if (!d) return "—";
  try {
    return formatWithTzLabel(d, "DD MMM YYYY, hh:mm A");
  } catch {
    return d || "—";
  }
};
const calcAge = (dob) => {
  if (!dob) return "—";
  const diff = Date.now() - new Date(dob).getTime();
  const a = Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
  return a > 0 ? a + " yrs" : "—";
};
const examYearOrder = (y) => EXAM_YEARS.indexOf(y);

// ── shared field sx ───────────────────────────────────────────────────────────
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    height: 40,
    ...typography.bodyLarge,
    color: inputColor,
    "& fieldset": { borderColor: borderColors.cardBorderPrimaryGrayColor },
    "&:hover fieldset": { borderColor: ColorsConstants.primaryGray50 },
    "&.Mui-focused fieldset": { borderColor: ColorsConstants.primaryPurple100 },
  },
};
const textAreaSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    ...typography.bodyLarge,
    color: inputColor,
    "& fieldset": { borderColor: borderColors.inputFieldGrey },
    "&:hover fieldset": { borderColor: ColorsConstants.primaryGray50 },
    "&.Mui-focused fieldset": { borderColor: ColorsConstants.primaryPurple100 },
  },
};

const FL = ({ label, children }) => (
  <Box>
    <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mb: 0.5 }}>
      {label}
    </Typography>
    {children}
  </Box>
);

const studentSchema = yup.object().shape({
  name: yup.string().trim().required("Name is required"),

  phone: yup
    .string()
    .required("Phone is required")
    .matches(/^\d{10}$/, "Enter valid 10-digit phone number"),

  batchId: yup.mixed().required("Batch is required"),
  dob: yup
    .date()
    .nullable()
    .transform((value, originalValue) => {
      return originalValue === "" ? null : value;
    })
    .notRequired(),
  joined: yup.string().nullable().notRequired(),
  schoolName: yup.string().nullable().notRequired(),
  schoolGrade: yup.string().nullable().notRequired(),
  fee: yup.object().shape({
    status: yup.string().nullable().notRequired(),

    amount: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .typeError("Amount must be a number")
      .positive("Amount must be greater than 0")
      .when("status", {
        is: (val) => val === "Paid",
        then: (schema) => schema.required("Amount is required"),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),

    due: yup
      .string()
      .transform((value) => (value === "" ? null : value))
      .when("status", {
        is: (val) => val === "Paid",
        then: (schema) => schema.required("Payment date is required"),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),

    mode: yup
      .string()
      .transform((value) => (value === "" ? null : value))
      .when("status", {
        is: (val) => val === "Paid",
        then: (schema) => schema.required("Payment mode is required"),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
  }),
  exams: yup
    .array()
    .of(
      yup.object().shape({
        examYear: yup.string().when("examDate", {
          is: (val) => !!val,
          then: (schema) => schema.required("Exam year is required"),
        }),
        examDate: yup.string().required("Exam date is required"),
      }),
    )
    .test(
      "unique-exam-year",
      "Each exam year must be unique",
      function (value) {
        if (!value || value.length === 0) return true;

        const seen = new Set();

        for (let v of value) {
          if (seen.has(v.examYear)) {
            return this.createError({
              path: "exams",
              message: "Each exam year must be unique",
            });
          }
          seen.add(v.examYear);
        }

        return true;
      },
    ),
});

// ── Status chip ───────────────────────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const map = {
    Paid: { bg: "#E8F5E9", color: dashboardCardGreenTextColor },
    Pending: { bg: "#FEF4E0", color: "#C07000" },
    Overdue: { bg: "#FDECEA", color: ColorsConstants.error },
  };
  const s = map[status] || map.Pending;
  return (
    <AppChip
      label={status}
      sx={{ backgroundColor: s.bg, color: s.color, fontWeight: 600 }}
    />
  );
};

const EnrollChip = ({ status }) => {
  const map = {
    Active: { bg: "#E8F5E9", color: dashboardCardGreenTextColor },
    Inactive: { bg: "#F3F4F6", color: ColorsConstants.primaryGray50 },
    Paused: { bg: "#FEF4E0", color: "#C07000" },
  };
  const s = map[status] || map.Active;
  return (
    <AppChip label={status} sx={{ backgroundColor: s.bg, color: s.color }} />
  );
};

const ExamYearChip = ({ year }) => (
  <AppChip
    label={year}
    sx={{ backgroundColor: "#E0F2F2", color: "#0A6B6B", fontWeight: 600 }}
  />
);

// ── Phone input ───────────────────────────────────────────────────────────────
const PhoneInput = ({ countryCode, phone, onCC, onPhone }) => (
  <Box
    sx={{
      display: "flex",
      border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
      borderRadius: "8px",
      overflow: "hidden",
      height: 40,
      alignItems: "center",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        px: 1.5,
        backgroundColor: "#F8F7F4",
        borderRight: `1px solid ${borderColors.inputFieldGrey}`,
        flexShrink: 0,
      }}
    >
      <Typography sx={{ fontSize: 16 }}>🇮🇳</Typography>
      <Select
        value={countryCode}
        onChange={onCC}
        variant="standard"
        disableUnderline
        sx={{
          ...typography.bodyLarge,
          fontWeight: 700,
          color: inputColor,
          minWidth: 60,
        }}
      >
        <MenuItem value="91">
          <Typography sx={typography.bodyLarge}>+91 India</Typography>
        </MenuItem>
        <MenuItem value="1">
          <Typography sx={typography.bodyLarge}>+1 USA</Typography>
        </MenuItem>
        <MenuItem value="44">
          <Typography sx={typography.bodyLarge}>+44 UK</Typography>
        </MenuItem>
        <MenuItem value="971">
          <Typography sx={typography.bodyLarge}>+971 UAE</Typography>
        </MenuItem>
      </Select>
    </Box>
    <TextField
      variant="standard"
      value={phone}
      onChange={onPhone}
      placeholder="98765 43210"
      inputProps={{ maxLength: 12 }}
      sx={{
        flex: 1,
        pl: 1.5,
        "& input": { ...typography.bodyLarge, color: inputColor },
        "& .MuiInput-underline:before": { display: "none" },
        "& .MuiInput-underline:after": { display: "none" },
      }}
    />
  </Box>
);

// ── Student Add/Edit Dialog (3 tabs) ─────────────────────────────────────────
const StudentDialog = ({
  open,
  onClose,
  onSave,
  editData,
  batch,
  allBatches,
  handleDeleteExam,
}) => {
  const blankS = {
    id: "",
    name: "",
    countryCode: "91",
    phone: "",
    dob: "",
    schoolName: "",
    schoolGrade: "",
    joined: "",
    enrollStatus: "Active",
    batchId: batch?.id || "",
  };
  const blankF = {
    feeType: "Yearly Fees",
    amount: "",
    due: "",
    mode: null,
    status: "Pending",
    duration: "2026-27",
    notes: "",
  };

  const [submitting, setSubmitting] = useState(false);
  const [s, setS] = useState(blankS);
  const [fee, setFee] = useState(blankF);
  const [tab, setTab] = useState(0);
  const [localExams, setLocalExams] = useState([]);
  const [newExam, setNewExam] = useState({
    examYear: "",
    examDate: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});

  const validateForm = async () => {
    try {
      const isFeeTouched =
        fee.amount !== "" ||
        fee.due !== "" ||
        fee.notes !== "" ||
        fee.status !== null ||
        fee.status !== "";

      const examsPayload = [
        ...localExams,
        ...(newExam.examDate ? [newExam] : []),
      ].filter((e) => e.examDate && e.examYear);
      const payload = {
        ...s,
        fee: isFeeTouched ? fee : undefined,
        exams: examsPayload,
      };

      if (newExam.examYear || newExam.examDate) {
        const newErrors = {};

        if (!newExam.examYear) {
          newErrors.examYear = "Exam year is required";
        }

        if (!newExam.examDate) {
          newErrors.examDate = "Exam date is required";
        }

        if (Object.keys(newErrors).length > 0) {
          setErrors((prev) => ({
            ...prev,
            ...newErrors,
          }));
          return false;
        }
      }

      let schema = studentSchema;

      if (!isFeeTouched) {
        schema = schema.shape({
          fee: yup.mixed().notRequired(),
        });
      }

      await schema.validate(payload, { abortEarly: false });

      setErrors({});
      return true;
    } catch (err) {
      const errObj = {};

      err.inner.forEach((e) => {
        if (!errObj[e.path]) {
          errObj[e.path] = e.message;
        }
      });
      setErrors(errObj);
      return false;
    }
  };

  useEffect(() => {
    if (open) {
      setSubmitting(false);
      setS(
        editData ? { ...editData } : { ...blankS, batchId: batch?.id || "" },
      );
      const latestFee = editData?.fee_records?.[0];

      setFee(
        latestFee
          ? {
              feeType: latestFee.fee_type || "",
              amount: latestFee.amount ? String(latestFee.amount) : "",
              due: latestFee.payment_date || "",
              mode: latestFee.payment_mode || "",
              status: latestFee.payment_status || "",
              duration: latestFee.academic_year || "",
              notes: latestFee.notes || "",
            }
          : { ...blankF },
      );
      setTab(0);
      setLocalExams(
        editData?.exam_records
          ? editData.exam_records.map((e) => ({
              id: e.id,
              studentId: e.student,
              examYear: e.exam_year_label,
              examDate: e.exam_date,
              notes: e.notes,
            }))
          : [],
      );
      setNewExam({ examYear: "", examDate: "", notes: "" });
    }
  }, [open]);

  const ss = (k) => (e) => {
    const value = e.target.value;

    setS((p) => ({ ...p, [k]: value }));

    // clear error for this field
    setErrors((prev) => ({
      ...prev,
      [k]: undefined,
    }));
  };

  const sf = (k) => (e) => {
    const value = e.target.value;

    setFee((p) => ({ ...p, [k]: value }));

    // clear nested fee errors
    setErrors((prev) => ({
      ...prev,
      [`fee.${k}`]: undefined,
    }));
  };

  const age = calcAge(s.dob);
  const curB = allBatches.find((b) => b.id === s.batchId) || batch;

  const addExam = () => {
    const newErrors = {};

    if (!newExam.examYear) {
      newErrors.examYear = "Exam year is required";
    }

    if (!newExam.examDate) {
      newErrors.examDate = "Exam date is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors((prev) => ({
        ...prev,
        ...newErrors,
      }));
      return;
    }

    const exists = localExams.some((e) => e.examYear === newExam.examYear);

    if (exists) {
      setErrors((prev) => ({
        ...prev,
        exams: "This exam year already exists",
      }));
      return;
    }

    // clear errors if valid
    setErrors((prev) => ({
      ...prev,
      exams: undefined,
      examDate: undefined,
    }));

    setLocalExams((p) => [
      ...p,
      {
        id: "E-NEW-" + Date.now(),
        studentId: s.id || "TEMP",
        examYear: newExam.examYear,
        examDate: newExam.examDate,
        notes: newExam.notes,
      },
    ]);

    setNewExam({ examYear: "", examDate: "", notes: "" });
  };

  const tabLabels = [
    "Student Info",
    "Fee Details",
    `Exams${localExams.length > 0 ? ` (${localExams.length})` : ""}`,
  ];

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose();
        setErrors({});
        setSubmitting(false);
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography sx={typography.cardTitle}>
          {editData ? "Edit Student" : "Add Student"}
        </Typography>
        {curB && (
          <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
            Batch: {curB.name} — {curB.centre_name}
          </Typography>
        )}
      </DialogTitle>

      <Box sx={{ px: { xs: 1, sm: 3 } }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: `1px solid ${borderColors.inputFieldGrey}`,
            "& .MuiTabs-indicator": {
              backgroundColor: ColorsConstants.primaryPurple100,
            },
          }}
        >
          {tabLabels.map((label, i) => (
            <Tab
              key={i}
              label={
                <Typography
                  sx={{ ...typography.bodyLarge, textTransform: "none" }}
                >
                  {label}
                </Typography>
              }
              sx={{
                "&.Mui-selected": { color: ColorsConstants.primaryPurple100 },
                color: grayFontColor,
              }}
            />
          ))}
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 2 }}>
        {/* ── Tab 0: Student Info ── */}
        {tab === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FL label="Full Name">
                <TextField
                  fullWidth
                  size="small"
                  value={s.name}
                  onChange={(e) => {
                    setS((p) => ({
                      ...p,
                      name: e.target.value,
                      ini: mkIni(e.target.value),
                    }));
                    setErrors((prev) => ({
                      ...prev,
                      name: undefined,
                    }));
                  }}
                  placeholder="Full name"
                  sx={fieldSx}
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </FL>
            </Grid>
            {/* <Grid item xs={6}><FL label="Student ID"><TextField fullWidth size="small" value={s.id} onChange={ss("id")} placeholder="KAL-026" sx={fieldSx} /></FL></Grid> */}
            <Grid item xs={12} sm={6}>
              <FL label="Batch">
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <Select
                    value={s.batchId}
                    onChange={ss("batchId")}
                    displayEmpty
                    error={!!errors.batchId}
                    helperText={errors.batchId}
                  >
                    {allBatches.map((b) => (
                      <MenuItem key={b.id} value={b.id}>
                        <Typography sx={typography.bodyLarge}>
                          {b.name} — {b.centre_name}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </FL>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FL label="Phone Number">
                <PhoneInput
                  countryCode={s.countryCode}
                  phone={s.phone}
                  onCC={ss("countryCode")}
                  onPhone={ss("phone")}
                />
                {errors.phone && (
                  <Typography color="error" variant="caption">
                    {errors.phone}
                  </Typography>
                )}
              </FL>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FL label="Date of Birth">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <AppDatePicker
                    value={s.dob ? dayjs(s.dob) : null}
                    onChange={(v) => {
                      setS((p) => ({
                        ...p,
                        dob: v ? v.format("YYYY-MM-DD") : "",
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        dob: undefined,
                      }));
                    }}
                    error={!!errors.dob}
                    helperText={errors.dob}
                  />
                </LocalizationProvider>
              </FL>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FL label="Age">
                <Box
                  sx={{
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    px: 1.5,
                    backgroundColor: "#F8F7F4",
                    border: `1px solid ${borderColors.inputFieldGrey}`,
                    borderRadius: "8px",
                  }}
                >
                  <Typography
                    sx={{
                      ...typography.bodyLarge,
                      color: age === "—" ? grayFontColor : inputColor,
                      fontWeight: age === "—" ? 400 : 600,
                    }}
                  >
                    {age === "—" ? "Auto from DOB" : age}
                  </Typography>
                </Box>
              </FL>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FL label="School Name">
                <TextField
                  fullWidth
                  size="small"
                  value={s.schoolName || ""}
                  onChange={ss("schoolName")}
                  placeholder="Enter school name"
                  sx={fieldSx}
                  error={!!errors.schoolName}
                  helperText={errors.schoolName}
                />
              </FL>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FL label="School Grade">
                <TextField
                  fullWidth
                  size="small"
                  value={s.schoolGrade || ""}
                  onChange={ss("schoolGrade")}
                  placeholder="Enter grade (e.g. Class 5)"
                  sx={fieldSx}
                  error={!!errors.schoolGrade}
                  helperText={errors.schoolGrade}
                />
              </FL>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FL label="Enrolment Status">
                <FormControl
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  error={!!errors.enrollStatus}
                  helperText={errors.enrollStatus}
                >
                  <Select value={s.enrollStatus} onChange={ss("enrollStatus")}>
                    <MenuItem value="Active">
                      <Typography sx={typography.bodyLarge}>Active</Typography>
                    </MenuItem>
                    <MenuItem value="Inactive">
                      <Typography sx={typography.bodyLarge}>
                        Inactive
                      </Typography>
                    </MenuItem>
                    <MenuItem value="Paused">
                      <Typography sx={typography.bodyLarge}>Paused</Typography>
                    </MenuItem>
                  </Select>
                </FormControl>
              </FL>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FL label="Joined (Date & Time)">
                <TextField
                  fullWidth
                  size="small"
                  type="datetime-local"
                  value={s.joined ? s.joined.substring(0, 16) : ""}
                  onChange={(e) => {
                    setS((p) => ({ ...p, joined: e.target.value + ":00" }));
                    setErrors((prev) => ({
                      ...prev,
                      joined: undefined,
                    }));
                  }}
                  sx={fieldSx}
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.joined}
                  helperText={errors.joined}
                />
              </FL>
            </Grid>
          </Grid>
        )}

        {/* ── Tab 1: Fee Details ── */}
        {tab === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FL label="Fee Type">
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <Select
                    value={fee.feeType}
                    onChange={(e) => {
                      setFee((p) => ({
                        ...p,
                        feeType: e.target.value,
                        amount:
                          e.target.value === "Exam Fees"
                            ? "800"
                            : e.target.value === "Concert Fees"
                              ? "1200"
                              : "3500",
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        "fee.feeType": undefined,
                      }));
                    }}
                    error={!!errors.feeType}
                    helperText={errors.feeType}
                  >
                    {FEE_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        <Typography sx={typography.bodyLarge}>{t}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </FL>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FL label="Amount (₹)">
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={fee.amount}
                  onChange={sf("amount")}
                  sx={fieldSx}
                  error={!!errors["fee.amount"]}
                  helperText={errors["fee.amount"]}
                />
              </FL>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FL label="Payment Date">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <AppDatePicker
                    value={fee.due ? dayjs(fee.due) : null}
                    onChange={(v) => {
                      const value = v ? v.format("YYYY-MM-DD") : "";

                      setFee((p) => ({
                        ...p,
                        due: value,
                      }));

                      setErrors((prev) => ({
                        ...prev,
                        "fee.due": undefined,
                      }));
                    }}
                    error={!!errors["fee.due"]}
                    helperText={errors["fee.due"]}
                  />
                </LocalizationProvider>
              </FL>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FL label="Payment Mode">
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <Select
                    value={fee.mode}
                    onChange={sf("mode")}
                    error={!!errors["fee.mode"]}
                  >
                    {PAY_MODES.map((m) => (
                      <MenuItem key={m} value={m}>
                        <Typography sx={typography.bodyLarge}>{m}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error={!!errors["fee.mode"]}>
                    {errors["fee.mode"]}
                  </FormHelperText>
                </FormControl>
              </FL>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FL label="Payment Status">
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <Select
                    value={fee.status}
                    onChange={sf("status")}
                    error={!!errors.status}
                    helperText={errors.status}
                  >
                    <MenuItem value="Paid">
                      <Typography sx={typography.bodyLarge}>Paid</Typography>
                    </MenuItem>
                    <MenuItem value="Pending">
                      <Typography sx={typography.bodyLarge}>Pending</Typography>
                    </MenuItem>
                    <MenuItem value="Overdue">
                      <Typography sx={typography.bodyLarge}>Overdue</Typography>
                    </MenuItem>
                  </Select>
                </FormControl>
              </FL>
            </Grid>
            <Grid item xs={12}>
              <FL label="Academic Year">
                <FormControl
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  error={!!errors.duration}
                  helperText={errors.duration}
                >
                  <Select value={fee.duration} onChange={sf("duration")}>
                    {DURATIONS.map((d) => (
                      <MenuItem key={d} value={d}>
                        <Typography sx={typography.bodyLarge}>{d}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </FL>
            </Grid>
            <Grid item xs={12}>
              <FL label="Notes">
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  value={fee.notes}
                  onChange={sf("notes")}
                  placeholder="UPI ref, cheque no., remarks…"
                  sx={textAreaSx}
                />
              </FL>
            </Grid>
          </Grid>
        )}

        {/* ── Tab 2: Exams ── */}
        {tab === 2 && (
          <Box>
            {localExams.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    ...typography.bodySmall,
                    color: grayFontColor,
                    mb: 1,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  Exam Records
                </Typography>
                {[...localExams]
                  .sort(
                    (a, c) =>
                      examYearOrder(a.examYear) - examYearOrder(c.examYear),
                  )
                  .map((e) => (
                    <Box
                      key={e.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        py: 1,
                        borderBottom: `1px solid ${borderColors.inputFieldGrey}`,
                      }}
                    >
                      <ExamYearChip year={e.examYear} />
                      <Typography sx={{ ...typography.bodyLarge, flex: 1 }}>
                        {fmtDate(e.examDate)}
                      </Typography>
                      <Typography
                        sx={{
                          ...typography.bodySmall,
                          color: grayFontColor,
                          flex: 1,
                        }}
                      >
                        {e.notes || "—"}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={async () => {
                          if (String(e.id).startsWith("E-NEW-")) {
                            // 🟢 Just local
                            setLocalExams((p) =>
                              p.filter((x) => x.id !== e.id),
                            );
                          } else {
                            // 🔴 Exists in DB → delete from backend
                            await handleDeleteExam(e.id);

                            // remove from UI after success
                            setLocalExams((p) =>
                              p.filter((x) => x.id !== e.id),
                            );
                          }
                        }}
                      >
                        <DeleteOutlineIcon
                          fontSize="small"
                          sx={{ color: ColorsConstants.error }}
                        />
                      </IconButton>
                    </Box>
                  ))}
              </Box>
            )}

            <Typography
              sx={{
                ...typography.bodySmall,
                color: grayFontColor,
                mb: 1.5,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              Add Exam Record
            </Typography>
            <Box
              sx={{
                backgroundColor: dashboardCardBgColor,
                borderRadius: "8px",
                p: 2,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FL label="Exam Year">
                    <FormControl fullWidth size="small" sx={fieldSx}>
                      <Select
                        value={newExam.examYear}
                        onChange={(e) => {
                          setNewExam((p) => ({
                            ...p,
                            examYear: e.target.value,
                          }));
                          setErrors((prev) => ({
                            ...prev,
                            examYear: undefined,
                          }));
                        }}
                        error={!!errors.examYear}
                        helperText={errors.examYear}
                      >
                        {EXAM_YEARS.map((y) => (
                          <MenuItem key={y} value={y}>
                            <Typography sx={typography.bodyLarge}>
                              {y}
                            </Typography>
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography color="error" variant="caption">
                        {errors.examYear}
                      </Typography>
                    </FormControl>
                  </FL>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FL label="Exam Date">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <AppDatePicker
                        value={
                          newExam.examDate ? dayjs(newExam.examDate) : null
                        }
                        onChange={(v) => {
                          setNewExam((p) => ({
                            ...p,
                            examDate: v ? v.format("YYYY-MM-DD") : "",
                          }));
                          setErrors((prev) => ({
                            ...prev,
                            examDate: undefined,
                          }));
                        }}
                        error={!!errors.examDate}
                        helperText={errors.examDate}
                      />
                    </LocalizationProvider>
                  </FL>
                </Grid>
                <Grid item xs={12}>
                  <FL label="Notes">
                    <TextField
                      fullWidth
                      size="small"
                      value={newExam.notes}
                      onChange={(e) =>
                        setNewExam((p) => ({ ...p, notes: e.target.value }))
                      }
                      placeholder="e.g. Passed, Merit, Distinction"
                      sx={fieldSx}
                    />
                  </FL>
                </Grid>
              </Grid>
              <Box sx={{ mt: 1.5 }}>
                <ButtonOne startIcon={<AddIcon />} onClick={addExam}>
                  Add Exam Record
                </ButtonOne>
              </Box>
            </Box>

            {errors.exams && (
              <Typography color="error" variant="caption">
                {errors.exams}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2, gap: 1, flexWrap: "wrap" }}>
        <ButtonOne variant="outlined" onClick={onClose}>
          Cancel
        </ButtonOne>
        <ButtonOne
          disabled={submitting}
          onClick={async () => {
            const isValid = await validateForm();
            if (!isValid) return;

            const examsToSend = localExams.filter((e) =>
              String(e.id).startsWith("E-NEW-"),
            );

            if (newExam.examDate && newExam.examYear) {
              examsToSend.push({
                id: "E-NEW-" + Date.now(),
                studentId: s.id || "TEMP",
                examYear: newExam.examYear,
                examDate: newExam.examDate,
                notes: newExam.notes,
              });
            }
            setSubmitting(true);
            try {
              await onSave(s, fee, examsToSend);
              onClose(); // ✅ only place that closes the modal
            } catch (err) {
              console.error("Save failed:", err);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {submitting ? "Saving…" : editData ? "Save Changes" : "Add Student"}
        </ButtonOne>
      </DialogActions>
    </Dialog>
  );
};

// ── Student View Dialog ───────────────────────────────────────────────────────'
const StudentViewDialog = ({ open, onClose, student }) => {
  const [vtab, setVtab] = useState(0);
  if (!student) return null;

  const enrol = student.enrolments?.[0] || {};

  const fees = student.fee_records || [];
  const exams = [...(student.exam_records || [])].sort(
    (a, b) => a.exam_year - b.exam_year,
  );

  const paidAmt = fees
    .filter((f) => f.payment_status === "Paid")
    .reduce((a, r) => a + r.amount, 0);

  const InfoCell = ({ label, value, chip }) => (
    <Box
      sx={{
        backgroundColor: dashboardCardBgColor,
        borderRadius: "8px",
        p: 1.5,
      }}
    >
      <Typography
        sx={{
          ...typography.bodySmall,
          color: grayFontColor,
          mb: 0.5,
          textTransform: "uppercase",
          letterSpacing: "0.07em",
        }}
      >
        {label}
      </Typography>
      {chip || (
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 500 }}>
          {value || "—"}
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      {/* HEADER */}
      <DialogTitle sx={{ pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            p: 2,
            backgroundColor: dashboardCardBgColor,
            borderRadius: "10px",
            border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
          }}
        >
          {/* Avatar */}
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              backgroundColor: enrol.batch_color
                ? enrol.batch_color + "20"
                : ColorsConstants.primaryBlue60,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1.5px solid ${enrol.batch_color || ColorsConstants.primaryPurple100}40`,
            }}
          >
            <Typography
              sx={{
                ...typography.bodyLarge,
                fontWeight: 800,
                color: enrol.batch_color || ColorsConstants.primaryPurple100,
              }}
            >
              {student.initials}
            </Typography>
          </Box>

          {/* Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={typography.cardTitle}>{student.name}</Typography>

            <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
              {student.student_code} · {enrol.batch_name || "—"}
            </Typography>

            <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
              +{student.country_code} {student.phone}
            </Typography>

            <Box sx={{ mt: 0.5 }}>
              <EnrollChip status={enrol.enrol_status} />
            </Box>
          </Box>

          {/* Paid */}
          <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
            <Typography
              sx={{
                ...typography.statExtraSmall,
                color: dashboardCardGreenTextColor,
              }}
            >
              {inr(paidAmt)}
            </Typography>
            <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
              total paid
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      {/* TABS */}
<Box sx={{ px: 3 }}>
  <Tabs
    value={vtab}
    onChange={(_, v) => setVtab(v)}
    sx={{
      "& .MuiTabs-indicator": {
        height: 3,
        backgroundColor: ColorsConstants.primaryPurple100,
      },
    }}
  >
    {["Info", `Fees (${fees.length})`, `Exams (${exams.length})`].map(
      (label, i) => (
        <Tab
          key={i}
          label={label}
          sx={{
            textTransform: "none",
            color: grayFontColor,
            fontWeight: 500,

            "&.Mui-selected": {
              color: ColorsConstants.primaryPurple100,
              fontWeight: 600,
            },

            "&:hover": {
              color: ColorsConstants.primaryPurple100,
              cursor: "pointer",
            },
          }}
        />
      ),
    )}
  </Tabs>
</Box>

      <DialogContent>
        {/* INFO TAB */}
        {vtab === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Personal */}
            <Box>
              <Typography
                sx={{ ...typography.bodySmall, color: grayFontColor, mb: 1 }}
              >
                PERSONAL INFO
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <InfoCell
                    label="Phone"
                    value={`+${student.country_code} ${student.phone}`}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoCell label="Email" value={student.email || "—"} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoCell
                    label="DOB"
                    value={student.dob ? fmtDate(student.dob) : "—"}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoCell label="School Name" value={student.school_name} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoCell label="Grade" value={student.school_grade} />
                </Grid>
              </Grid>
            </Box>

            {/* Batch */}
            <Box>
              <Typography
                sx={{ ...typography.bodySmall, color: grayFontColor, mb: 1 }}
              >
                BATCH INFO
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <InfoCell label="Batch" value={enrol.batch_name} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoCell label="Centre" value={enrol.centre_name} />
                </Grid>
                <Grid item xs={12}>
                  <InfoCell label="Joined" value={enrol.joined_at ? fmtDT(enrol.joined_at) : "-"} />
                </Grid>
              </Grid>
            </Box>

            {/* System */}
            <Box>
              <Typography
                sx={{ ...typography.bodySmall, color: grayFontColor, mb: 1 }}
              >
                SYSTEM INFO
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <InfoCell label="Updated By" value={student.updated_by} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoCell
                    label="Updated At"
                    value={fmtDT(student.updated_at)}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}

        {/* FEES TAB */}
        {vtab === 1 &&
          (fees.length === 0 ? (
            <Typography sx={{ color: grayFontColor }}>
              No fee records
            </Typography>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <DashboardBadge
                  value={inr(paidAmt)}
                  label="Total Paid"
                  textColor={dashboardCardGreenTextColor}
                />
              </Box>

              <AppTable
                columns={[
                  { id: "fee_type", label: "Type" },
                  {
                    id: "amount",
                    label: "Amount",
                    render: (r) => inr(r.amount),
                  },
                  {
                    id: "payment_date",
                    label: "Payment Date",
                    render: (r) => fmtDate(r.payment_date),
                  },
                  { id: "payment_mode", label: "Mode" },
                  { id: "academic_year", label: "Year" },
                  {
                    id: "payment_status",
                    label: "Status",
                    render: (r) => <StatusChip status={r.payment_status} />,
                  },
                ]}
                rows={fees}
                rowKey="id"
              />
            </>
          ))}

        {/* EXAMS TAB */}
        {vtab === 2 &&
          (exams.length === 0 ? (
            <Typography sx={{ color: grayFontColor }}>
              No exam records
            </Typography>
          ) : (
            <AppTable
              columns={[
                { id: "exam_year_label", label: "Year" },
                {
                  id: "exam_date",
                  label: "Date",
                  render: (r) => fmtDate(r.exam_date),
                },
                { id: "notes", label: "Notes" },
              ]}
              rows={exams}
              rowKey="id"
            />
          ))}
      </DialogContent>

      <DialogActions>
        <ButtonOne variant="outlined" onClick={onClose}>
          Close
        </ButtonOne>
      </DialogActions>
    </Dialog>
  );
};

// ── Fee Edit Dialog ───────────────────────────────────────────────────────────
const FeeEditDialog = ({ open, onClose, onSave, editData, studentName }) => {
  const blank = {
    feeType: "Yearly Fees",
    amount: "3500",
    due: "",
    mode: "UPI",
    status: "Pending",
    duration: "2026-27",
    notes: "",
  };
  const [f, setF] = useState(blank);
  useEffect(() => {
    setF(editData ? { ...editData, amount: String(editData.amount) } : blank);
  }, [open]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <DialogTitle>
        <Typography sx={typography.cardTitle}>Edit Fee Record</Typography>
        {studentName && (
          <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
            {studentName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12}>
            <FL label="Fee Type">
              <FormControl fullWidth size="small" sx={fieldSx}>
                <Select
                  value={f.feeType}
                  onChange={(e) =>
                    setF((p) => ({
                      ...p,
                      feeType: e.target.value,
                      amount:
                        e.target.value === "Exam Fees"
                          ? "800"
                          : e.target.value === "Concert Fees"
                            ? "1200"
                            : "3500",
                    }))
                  }
                >
                  {FEE_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      <Typography sx={typography.bodyLarge}>{t}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </FL>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FL label="Amount (₹)">
              <TextField
                fullWidth
                size="small"
                type="number"
                value={f.amount}
                onChange={set("amount")}
                sx={fieldSx}
              />
            </FL>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FL label="Payment Date">
              <TextField
                fullWidth
                size="small"
                type="date"
                value={f.due}
                onChange={set("due")}
                sx={fieldSx}
                InputLabelProps={{ shrink: true }}
              />
            </FL>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FL label="Payment Mode">
              <FormControl fullWidth size="small" sx={fieldSx}>
                <Select value={f.mode} onChange={set("mode")}>
                  {PAY_MODES.map((m) => (
                    <MenuItem key={m} value={m}>
                      <Typography sx={typography.bodyLarge}>{m}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </FL>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FL label="Status">
              <FormControl fullWidth size="small" sx={fieldSx}>
                <Select value={f.status} onChange={set("status")}>
                  <MenuItem value="Paid">
                    <Typography sx={typography.bodyLarge}>Paid</Typography>
                  </MenuItem>
                  <MenuItem value="Pending">
                    <Typography sx={typography.bodyLarge}>Pending</Typography>
                  </MenuItem>
                </Select>
              </FormControl>
            </FL>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FL label="Academic Year">
              <FormControl fullWidth size="small" sx={fieldSx}>
                <Select value={f.duration} onChange={set("duration")}>
                  {DURATIONS.map((d) => (
                    <MenuItem key={d} value={d}>
                      <Typography sx={typography.bodyLarge}>{d}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </FL>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FL label="Notes">
              <TextField
                fullWidth
                multiline
                minRows={2}
                value={f.notes}
                onChange={set("notes")}
                placeholder="Remarks…"
                sx={textAreaSx}
              />
            </FL>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <ButtonOne variant="outlined" onClick={onClose}>
          Cancel
        </ButtonOne>
        <ButtonOne onClick={() => onSave(f)}>Save Record</ButtonOne>
      </DialogActions>
    </Dialog>
  );
};

// ── Expanded fee rows panel ───────────────────────────────────────────────────
const ExpandedFees = ({ fees, onMarkPaid, onEditFee, onDeleteFee }) => {
  const feeColumns = [
    {
      id: "feeType",
      label: "Type",
      render: (r) => (
        <AppChip
          label={
            r.feeType === "Exam Fees"
              ? "Exam"
              : r.feeType === "Concert Fees"
                ? "Concert"
                : "Yearly"
          }
          sx={{
            backgroundColor: ColorsConstants.primaryBlue60,
            color: ColorsConstants.primaryPurple100,
          }}
        />
      ),
    },
    {
      id: "amount",
      label: "Amount",
      render: (r) => (
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
          {inr(r.amount)}
        </Typography>
      ),
    },
    {
      id: "due",
      label: "Payment Date",
      render: (r) => (
        <Typography
          sx={{
            ...typography.bodySmall,
            color: isOD(r.due, r.status)
              ? ColorsConstants.error
              : grayFontColor,
          }}
        >
          {fmtDate(r.due)}
        </Typography>
      ),
    },
    {
      id: "mode",
      label: "Mode",
      render: (r) => (
        <Typography sx={typography.bodySmall}>{r.mode}</Typography>
      ),
    },
    {
      id: "duration",
      label: "Year",
      render: (r) => (
        <Typography sx={{ ...typography.bodySmall, fontWeight: 600 }}>
          {r.duration}
        </Typography>
      ),
    },
    {
      id: "notes",
      label: "Notes",
      render: (r) => (
        <Typography
          sx={{
            ...typography.bodySmall,
            color: grayFontColor,
            maxWidth: 120,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {r.notes || "—"}
        </Typography>
      ),
    },
    {
      id: "status",
      label: "Status",
      render: (r) => <StatusChip status={effSt(r)} />,
    },
    {
      id: "actions",
      label: "",
      align: "right",
      render: (r) => {
        const es = effSt(r);
        return (
          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end", flexWrap: "wrap" }}>
            {es !== "Paid" && (
              <IconButton
                size="small"
                title="Mark Paid"
                onClick={() => onMarkPaid(r.id)}
              >
                <CheckCircleOutlineIcon
                  fontSize="small"
                  sx={{ color: dashboardCardGreenTextColor }}
                />
              </IconButton>
            )}
            <IconButton size="small" title="Edit" onClick={() => onEditFee(r)}>
              <EditOutlinedIcon
                fontSize="small"
                sx={{ color: ColorsConstants.primaryGray60 }}
              />
            </IconButton>
            <IconButton
              size="small"
              title="Delete"
              onClick={() => onDeleteFee(r.id)}
            >
              <DeleteOutlineIcon
                fontSize="small"
                sx={{ color: ColorsConstants.error }}
              />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  if (fees.length === 0) {
    return (
      <Box sx={{ px: 3, py: 2 }}>
        <Typography sx={{ ...typography.bodyLarge, color: grayFontColor }}>
          No fee records yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, pb: 2, backgroundColor: "#F9FAFB" }}>
      <AppTable
        columns={feeColumns}
        rows={fees}
        rowKey="id"
        maxHeight={300}
        paperSx={{ mb: 0, border: "none" }}
      />
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// KalanjayBatchDetail
// Props:
//   batch / batches / students / fees / exams
//   setStudents / setFees / setExams
//   onBack — () => void
//   showToast — (msg) => void
// ══════════════════════════════════════════════════════════════════════════════
const KalanjayBatchDetail = ({
  batch,
  fees = [],
  exams = [],
  onBack,
  showToast,
  lastViewedPage,
  setLastViewedPage,
  setActiveTab
}) => {
  const permissions = localStorage.getItem("permissions") || [];
  const hasViewPermission = permissions.includes("batch_details.view");
  const hasCreatePermission = permissions.includes("batch_details.create");
  const hasEditPermission = permissions.includes("batch_details.edit");

  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch] = useState("");

  const [expandedId, setExpandedId] = useState(null);
  const [sModal, setSModal] = useState(false);
  const [sEdit, setSEdit] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [viewStudent, setViewStudent] = useState(null);
  const [fEditModal, setFEditModal] = useState(false);
  const [fEditData, setFEditData] = useState(null);
  const [fEditStudent, setFEditStudent] = useState(null);
  const [stats, setStats] = useState({
    total_students: 0,
    capacity: 0,
    total_paid_amount: 0,
    total_paid_amount_cash: 0,
    total_paid_amount_cheque: 0,
    pending_count: 0,
    pending_amount: 0,
    overdue_count: 0,
    overdue_amount: 0,
    unpaid_count: 0,
    loading: false,
  });

  const bStudents = students.length;
  const loading = false;
  const { getValidToken } = useAuth();

  useEffect(() => {
    if (batch?.id) {
      fetchStudents(batch.id, 20, 0, search);
      fetchBatchStats(batch.id);
      fetchBatches(999, 0);
    }
  }, [batch]);

  const fetchStudents = async (
    batchId = null,
    limit = 20,
    offset = 0,
    search = "",
  ) => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      const params = new URLSearchParams({ limit, offset });
      if (search) params.append("search", search);

      const url = `${GET_KALANJAY_BATCH_STUDENTS(batchId)}?${params.toString()}`;

      const res = await axios.get(url, { headers });

      const data = res?.data?.results || res.data;
      const count = res?.data?.count ?? data.length;
      setStudents(data);
      setTotalCount(count);
    } catch (err) {
      console.error("Error fetching students:", err);
    }
  };

  const fetchBatches = async (
    limit = 20,
    offset = 0,
    search = "",
    centreId = "",
  ) => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    try {
      const params = new URLSearchParams({ limit, offset });
      if (search) params.append("search", search);
      if (centreId) params.append("centre", centreId);

      const res = await axios.get(
        `${GET_KALANJAY_BATCHES}?${params.toString()}`,
        { headers },
      );
      const batchesFromApi = res?.data?.results || res.data;
      const count = res?.data?.count ?? batchesFromApi.length;
      setBatches(batchesFromApi);
    } catch (err) {
      console.error(err);
    }
  };

  const onPageChange = (_, newPage) => {
    setPage(newPage);
    fetchStudents(batch?.id, rowsPerPage, newPage * rowsPerPage, search);
  };

  const onRowsPerPageChange = (e) => {
    const val = Number(e?.target?.value);
    if (!val || isNaN(val)) return;
    setRowsPerPage(val);
    setPage(0);
    fetchStudents(batch?.id, val, 0, search);
  };

  const onSearchChange = (val) => {
    setSearch(val);
    setPage(0);
    fetchStudents(batch?.id, rowsPerPage, 0, val);
  };

  const handleUpdateFee = async (feeId, fee, studentId, batchId) => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    await axios.put(
      UPDATE_KALANJAY_FEE(feeId),
      {
        student: studentId,
        batch: batchId,
        fee_type: fee.feeType,
        amount: Number(fee.amount),
        payment_date: fee.due || null,
        payment_mode: fee.mode,
        payment_status: fee.status,
        academic_year: fee.duration,
        notes: fee.notes,
      },
      { headers },
    );
  };

  const handleSaveStudent = async (s, fee, exams) => {
    try {
      if (!s.id) {
        if (stats.total_students >= stats.capacity) {
          showToast?.("Batch capacity limit reached");
          return;
        }
      }

      if (s.id) {
        await handleUpdateStudent(s);
        const feeRecord = sEdit?.fee_records?.[0];
        if (fee) {
          if (feeRecord?.id) {
            // ✅ UPDATE existing fee
            await handleUpdateFee(feeRecord.id, fee, s.id, s.batchId);
          } else {
            // ✅ CREATE new fee
            await handleCreateFee(fee, s.id, s.batchId);
          }
        }
        const newExams = exams.filter((e) => String(e.id).startsWith("E-NEW-"));

        if (newExams.length > 0) {
          await handleCreateExams(s.id, s.batchId, newExams);
        }
      } else {
        await handleAddStudent(s, fee, exams);
      }

      await fetchStudents(batch?.id, rowsPerPage, page * rowsPerPage, search);
      fetchBatchStats(batch.id);
      setSModal(false);
      setSEdit(null);
    } catch (err) {
      console.error("Save student failed:", err);
      setSModal(false);
      setSEdit(null);
    }
  };

  const handleDeleteExam = async (examId) => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    await axios.delete(DELETE_KALANJAY_EXAM(examId), { headers });
  };

  const handleCreateFee = async (fee, studentId, batchId) => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    await axios.post(
      CREATE_KALANJAY_FEE,
      {
        student: studentId,
        batch: batchId,
        fee_type: fee.feeType,
        amount: Number(fee.amount),
        payment_date: fee.due || null,
        payment_mode: fee.mode,
        payment_status: fee.status,
        academic_year: fee.duration,
        notes: fee.notes,
      },
      { headers },
    );
  };

  const handleCreateExams = async (studentId, batchId, exams) => {
    if (!exams || exams.length === 0) return;

    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    for (const ex of exams) {
      await axios.post(
        CREATE_KALANJAY_EXAM,
        {
          student: studentId,
          batch: batchId,
          exam_year: examYearToNumber(ex.examYear),
          exam_date: ex.examDate,
          notes: ex.notes,
        },
        { headers },
      );
    }
  };

  const handleUpdateStudent = async (s) => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      await axios.patch(
        // 👈 use PATCH (better than PUT)
        UPDATE_KALANJAY_STUDENT(s.id),
        {
          name: s.name,
          country_code: s.countryCode,
          phone: s.phone,
          email: s.email || "",
          dob: s.dob || null,
          school_name: s.schoolName || "",
          school_grade: s.schoolGrade || "",
        },
        { headers },
      );
      console.log("Student updated:", s);
      if (s.enrolment_id) {
        await axios.patch(
          UPDATE_KALANJAY_BATCH_ENROLLMENT(s.enrolment_id),
          {
            enrol_status: s.enrollStatus,
            joined_at: s.joined,
            batch: s.batchId,
          },
          { headers },
        );
      }
      setSEdit(null);
    } catch (err) {
      console.error("Update failed:", err);
    }
  };

  const handleEditStudent = async (row) => {
    try {
      const data = await fetchStudentDetail(row.id, getValidToken);

      const enrol = data.enrolments?.[0] || {};
      const mapped = {
        id: data.id,
        name: data.name,
        countryCode: data.country_code,
        phone: data.phone,
        dob: data.dob,
        schoolName: data.school_name,
        schoolGrade: data.school_grade,

        batchId: enrol.batch,
        enrollStatus: enrol.enrol_status,
        joined: enrol.joined_at,
        enrolment_id: enrol.id,
        fee_records: data.fee_records,
        exam_records: data.exam_records,
        // optional
        email: data.email,
      };

      setSEdit(mapped);
      setSModal(true);
    } catch (err) {
      console.error("Failed to fetch student for edit:", err);
    }
  };

  const columns = [
    {
      id: "name",
      label: "Name",
      render: (row) => (
        <Box>
          <Typography sx={{ fontWeight: 600 }}>{row.name}</Typography>
          <Typography sx={{ fontSize: 12, color: grayFontColor }}>
            {row.student_code}
          </Typography>
        </Box>
      ),
    },
    {
      id: "phone_full",
      label: "Phone",
      render: (row) => row.phone_full || "-",
    },
    {
      id: "status",
      label: "Status",
      render: (row) =>
        row.status ? (
          <AppChip
            label={row.status}
            sx={{
              backgroundColor:
                row.status === "Active"
                  ? "#DCFCE7"
                  : row.status === "Paused"
                    ? "#FEF3C7"
                    : "#F3F4F6",

              color:
                row.status === "Active"
                  ? "#008026"
                  : row.status === "Paused"
                    ? "#D97706"
                    : "#9CA3AF",
            }}
          />
        ) : (
          "-"
        ),
    },

    // School Grade
    {
      id: "grade",
      label: "Grade",
      render: (row) => row.school_grade || "-",
    },

    // // Joined
    // {
    //   id: "joined",
    //   label: "Joined",
    //   render: (row) =>
    //     row.joined ? new Date(row.joined).toLocaleDateString("en-IN") : "-",
    // },

    // Current Year Fee
    {
      id: "current_fee",
      label: "Year Fee",
      render: (row) =>
        row.current_year_fee && row.current_year_fee.payment_status ? (
          <AppChip
            label={row.current_year_fee.payment_status}
            sx={{
              backgroundColor:
                row.current_year_fee.payment_status === "Paid"
                  ? "#DCFCE7"
                  : row.current_year_fee.is_overdue
                    ? "#FEE2E2"
                    : "#FEF3C7",
              color:
                row.current_year_fee.payment_status === "Paid"
                  ? "#008026"
                  : row.current_year_fee.is_overdue
                    ? "#DC2626"
                    : "#D97706",
            }}
          />
        ) : (
          "-"
        ),
    },

    // // Exam Fee
    // {
    //   id: "exam_fee",
    //   label: "Exam Fee",
    //   render: (row) =>
    //     row.exam_fee_status ? (
    //       <AppChip
    //         label={row.exam_fee_status.payment_status}
    //         sx={{
    //           backgroundColor:
    //             row.exam_fee_status.payment_status === "Paid"
    //               ? "#DCFCE7"
    //               : row.exam_fee_status.is_overdue
    //                 ? "#FEE2E2"
    //                 : "#FEF3C7",
    //           color:
    //             row.exam_fee_status.payment_status === "Paid"
    //               ? "#008026"
    //               : row.exam_fee_status.is_overdue
    //                 ? "#DC2626"
    //                 : "#D97706",
    //         }}
    //       />
    //     ) : (
    //       "-"
    //     ),
    // },

    // Last Exam
    {
      id: "exam",
      label: "Last Exam",
      render: (row) => (row.last_exam ? row.last_exam.exam_year_label : "-"),
    },

    // Updated By
    {
      id: "updated_by",
      label: "Updated By",
      render: (row) => row.updated_by || "—",
    },

    // Updated At
    {
      id: "updated_at",
      label: "Updated At",
      render: (row) => (row.updated_at ? fmtDT(row.updated_at) : "—"),
    },

    // Actions
    {
      id: "actions",
      label: "",
      align: "right",
      render: (row) => (
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <IconButton
            size="small"
            title="View"
            onClick={(e) => {
              e.stopPropagation();
              handleViewStudent(row);
            }}
            sx={{
              color: ColorsConstants.primaryPurple100,
              "&:hover": { backgroundColor: ColorsConstants.primaryBlue60 },
            }}
          >
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>

          <IconButton
            size="small"
            title="Edit"
            onClick={() => {
              if (hasEditPermission) {
                handleEditStudent(row);
              }
            }}
            sx={{
              color: hasEditPermission
                ? ColorsConstants.primaryGray60
                : ColorsConstants.primaryGray20,
              "&:hover": {
                backgroundColor: hasEditPermission
                  ? ColorsConstants.primaryBlue60
                  : ColorsConstants.primaryGray20,
              },
            }}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>

          <IconButton
            size="small"
            title="Delete"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteStudent(row);
            }}
            sx={{
              color: ColorsConstants.error,
              "&:hover": { backgroundColor: ColorsConstants.primaryBlue60 },
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const examYearToNumber = (label) => {
    const map = {
      "1st Year": 1,
      "2nd Year": 2,
      "3rd Year": 3,
      "4th Year": 4,
      "5th Year": 5,
      "6th Year": 6,
      "7th Year": 7,
      "8th Year": 8,
    };
    return map[label];
  };

  const handleAddStudent = async (s, fee, exams) => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      const payload = {
        name: s.name,
        country_code: s.countryCode,
        phone: s.phone,
        dob: s.dob || null,
        school_name: s.schoolName || "",
        school_grade: s.schoolGrade || "",
        batch_id: s.batchId || batch?.id,
        enrol_status: s.enrollStatus,
        joined_at: s.joined || null,
      };

      const res = await axios.post(CREATE_KALANJAY_STUDENT, payload, {
        headers,
      });

      const student = res.data;

      if (fee?.amount) {
        await axios.post(
          CREATE_KALANJAY_FEE,
          {
            student: student.id,
            batch: payload.batch_id,
            fee_type: fee.feeType,
            amount: Number(fee.amount),
            payment_date: fee.due || null,
            payment_mode: fee.mode,
            payment_status: fee.status,
            academic_year: fee.duration,
            notes: fee.notes,
          },
          { headers },
        );
      }

      for (const ex of exams) {
        await axios.post(
          CREATE_KALANJAY_EXAM,
          {
            student: student.id,
            batch: payload.batch_id,
            exam_year: examYearToNumber(ex.examYear),
            exam_date: ex.examDate,
            notes: ex.notes,
          },
          { headers },
        );
      }
    } catch (err) {
      console.error("Student create failed", err);
    }
  };

  const mapStudentForView = (data) => {
    const enrolment = data.enrolments?.[0] || {};

    const latestFee = data.fee_records?.[0] || null;

    const lastExam =
      data.exam_records?.length > 0
        ? [...data.exam_records].sort((a, b) => b.exam_year - a.exam_year)[0]
        : null;

    return {
      ...data,

      // 🔥 flatten joined_at
      joined_at: enrolment.joined_at,

      // 🔥 convert fee_records → latest_fee_status (UI expects this)
      latest_fee_status: latestFee
        ? {
            payment_status: latestFee.payment_status,
            amount: latestFee.amount,
          }
        : null,

      // 🔥 convert exam_records → last_exam
      last_exam: lastExam
        ? {
            exam_year_label: `Year ${lastExam.exam_year}`,
            exam_date: lastExam.exam_date,
          }
        : null,
    };
  };

  const fetchStudentDetail = async (studentId, getValidToken) => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    const res = await axios.get(GET_KALANJAY_STUDENT_DETAIL(studentId), {
      headers,
    });

    return res.data;
  };

  const handleViewStudent = async (row) => {
    try {
      const data = await fetchStudentDetail(row.id, getValidToken);

      // Map API → Dialog format
      const mapped = mapStudentForView(data);

      setViewStudent(mapped);
      setViewModal(true);
    } catch (err) {
      console.error("Failed to fetch student detail:", err);
    }
  };

  const handleDeleteStudent = async (student) => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const headers = {
        Authorization: `Bearer ${token}`,
        "X-Tenant-Id": tenant,
      };

      await axios.delete(DELETE_KALANJAY_STUDENT(student.id), { headers });

      await fetchStudents(batch?.id, rowsPerPage, page * rowsPerPage, search);
      fetchBatchStats(batch.id);
      showToast?.("Student deleted");
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const handleExportCSV = async () => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };
      const params = new URLSearchParams({ limit: 10000, offset: 0 });
      const url = `${GET_KALANJAY_BATCH_STUDENTS(batch.id)}?${params.toString()}`;
      const res = await axios.get(url, { headers });
      const all = res?.data?.results || res.data;

      const rows = [["Name", "Telephone Number", "Batch Name", "Branch"]];
      all.forEach((s) => {
        rows.push([
          s.name || "",
          s.phone_full || "",
          batch.name || "",
          batch.centre_name || "",
        ]);
      });

      const csv = rows
        .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${batch.name}_students.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  const fetchBatchStats = async (batchId) => {
    try {
      setStats((prev) => ({ ...prev, loading: true }));

      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const headers = {
        Authorization: `Bearer ${token}`,
        "X-Tenant-Id": tenant,
      };

      const res = await axios.get(GET_KALANJAY_BATCH_STATS(batchId), {
        headers,
      });

      setStats({
        ...res.data,
        loading: false,
      });
    } catch (err) {
      console.error("Failed to fetch batch stats:", err);
      setStats((prev) => ({ ...prev, loading: false }));
    }
  };

  // ── Custom table with expandable rows via renderAfterRow ──────────────────
  // We use customHead approach + body-level expansion via Collapse.
  // Since AppTable doesn't support renderAfterRow natively, we render our own
  // table body with Collapse rows and pass `customHead` to AppTable just for
  // the header, rendering the body ourselves.
  //
  // For clean integration with the design system we use Paper + TableContainer
  // from MUI directly here, matching AppTable's styles.
  const {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
  } = require("@mui/material");

  const handleExpand = async (row) => {
    if (expandedId === row.id) {
      setExpandedId(null);
      return;
    }
    if (!row.fee_records) {
      const data = await fetchStudentDetail(row.id, getValidToken);
      const mapped = mapStudentForView(data);

      setViewStudent(mapped);
      setStudents((prev) =>
        prev.map((s) => (s.id === row.id ? { ...s, ...data } : s)),
      );
    }

    setExpandedId(row.id);
  };

  const handleMarkPaid = async (fee) => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const headers = {
        Authorization: `Bearer ${token}`,
        "X-Tenant-Id": tenant,
      };

      await axios.post(MARK_KALANJAY_FEE_PAID(fee), {}, { headers });

      // update UI
      setStudents((prev) =>
        prev.map((s) => ({
          ...s,
          fee_records: s.fee_records?.map((f) =>
            f.id === fee ? { ...f, payment_status: "Paid" } : f,
          ),
        })),
      );

      showToast?.("Marked as Paid");
    } catch (err) {
      console.error("Mark Paid failed:", err);
    }
  };

  const handleDeleteFee = async (fee) => {
    const confirmDelete = window.confirm("Delete this fee record?");
    if (!confirmDelete) return;

    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const headers = {
        Authorization: `Bearer ${token}`,
        "X-Tenant-Id": tenant,
      };

      await axios.delete(DELETE_KALANJAY_FEE(fee), { headers });

      // update UI
      setStudents((prev) =>
        prev.map((s) => ({
          ...s,
          fee_records: s.fee_records?.filter((x) => x.id !== fee),
        })),
      );

      showToast?.("Fee deleted");
    } catch (err) {
      console.error("Delete Fee failed:", err);
    }
  };

  const handleEditFee = async (f) => {
    if (!fEditData) return;
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const headers = {
        Authorization: `Bearer ${token}`,
        "X-Tenant-Id": tenant,
        "Content-Type": "application/json",
      };

      const payload = {
        student: fEditData.student,
        batch: fEditData.batch,
        fee_type: f.feeType,
        amount: Number(f.amount),
        payment_date: f.due,
        payment_mode: f.mode,
        payment_status: f.status,
        academic_year: f.duration,
        notes: f.notes,
      };

      await axios.put(UPDATE_KALANJAY_FEE(fEditData.id), payload, { headers });

      await fetchStudents(batch?.id, rowsPerPage, page * rowsPerPage, search);

      setFEditModal(false);
      setFEditData(null);
      setFEditStudent(null);

      showToast?.("Fee updated");
    } catch (err) {
      console.error("Edit Fee failed:", err);
    }
  };

  if (!hasViewPermission) {
    return (
      <Stack sx={{ mt: 16 }}>
        <NoPermission />
      </Stack>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Back + Title */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3, flexWrap: "wrap", }}>
        <IconButton
          onClick={() => {
            onBack();
            if (lastViewedPage === "payment_report"){
              setLastViewedPage("")
              setActiveTab("payment_report")
            }
          }}
          sx={{
            border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
            borderRadius: "8px",
            p: 0.75,
            color: grayFontColor,
          }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: batch.color,
          }}
        />
        <Box>
          <Typography sx={typography.cardTitle}>{batch.name}</Typography>
          <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
            {batch.centre_name}
          </Typography>
        </Box>
        <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
          <ButtonOne
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={handleExportCSV}
          >
            Export CSV
          </ButtonOne>
          <ButtonOne
            startIcon={<AddIcon />}
            onClick={() => {
              setSEdit(null);
              setSModal(true);
            }}
            disabled={!hasCreatePermission}
          >
            Add Student
          </ButtonOne>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          {
            title: "Enrolled",
            value: stats.total_students,
            label: `Students`,
            textColor: batch.color,
          },
          {
            title: "Paid",
            value: inr(stats.total_paid_amount),
            label: `Cash ${inr(stats.total_paid_amount_cash)} \u00A0\u00A0\u00A0\u00A0\u00A0 Cheque ${inr(stats.total_paid_amount_cheque)}`,
            textColor: dashboardCardGreenTextColor,
          },
          {
            title: "Unpaid",
            value: stats.unpaid_count,
            label: "Entries",
            textColor: "#C07000",
          },
          // {
          //   title: "Overdue",
          //   value: stats.overdue_count,
          //   label: "Entries",
          //   textColor: ColorsConstants.error,
          // },
        ].map((item, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <DashboardBadge
              title={item.title}
              value={item.value}
              label={item.label}
              textColor={item.textColor}
              sx={{
                py: 1.5,
                gap: "4px",
              }}
            />
          </Grid>
        ))}
      </Grid>

      {/* Student table with expandable fee rows */}
      <Box
        sx={{
          mb: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography sx={typography.cardTitle}>
          Students ({bStudents})
        </Typography>
        <TextField
          size="Small"
          placeholder="Search by Name, Phone, Student ID"
          value={search} // ← controlled from parent
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: grayFontColor, fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{ ...fieldSx, width: 250 }}
        />
      </Box>

      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          borderRadius: "8px",
          mb: 2,
          boxShadow: "none"
        }}
      >
        <AppTable
          columns={columns}
          rows={students}
          count={totalCount}
          loading={loading}
          page={page}
          rowsPerPageOptions={[20, 50, 100]}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowClickable
          onRowClick={(row) => handleViewStudent(row)}
        />
      </Paper>

      {/* Dialogs */}
      <StudentDialog
        open={sModal}
        onClose={() => {
          setSModal(false);
          setSEdit(null);
        }}
        onSave={handleSaveStudent}
        editData={sEdit}
        batch={batch}
        allBatches={batches}
        getValidToken={getValidToken}
        handleDeleteExam={handleDeleteExam}
      />
      <FeeEditDialog
        open={fEditModal}
        onClose={() => {
          setFEditModal(false);
          setFEditData(null);
          setFEditStudent(null);
        }}
        onSave={handleEditFee}
        editData={fEditData}
        studentName={fEditStudent?.name}
      />
      <StudentViewDialog
        open={viewModal}
        onClose={() => {
          setViewModal(false);
          setViewStudent(null);
        }}
        student={viewStudent}
        fees={fees}
        exams={exams}
        batch={batch}
      />
    </Box>
  );
};

export default KalanjayBatchDetail;
        

          
